import '../../styles/blog.css';
import '../../styles/catalog.css';
import type { ReactNode } from 'react';
import { ArrowLeft } from '@phosphor-icons/react';
import { SiteLink as Link } from '../../components/SiteLink';
import { PageShell, GetStarted } from '../../components';
import { PageBody, PageHero, Prose, Section } from '../../components/page';
import { signupFrom } from '../../components/blog/Figure';
import { CATALOG, CATALOG_PATH, CATEGORY_LABELS, type CatalogServer } from '../../catalog';
import { SERVER_GUIDES, formatDate } from '../../content';
import { postComponents } from '../../components/mdx';
import { CodeBlock } from '../../components/tutorials/CodeBlock';
import { ServerCard, ServerIcon } from './ServerCard';

const list = (items: string[]) => items.join(', ');

const guideComponents = { ...postComponents, pre: CodeBlock };

/** Who runs it, how it connects, what it asks for and where it may connect. */
function facts(s: CatalogServer): [string, ReactNode][] {
  const vendor = s.publisher ?? s.name;
  const rows: [string, ReactNode][] = [
    ['Runs on', s.hosting === 'statio' ? "Statio's runtime" : `${vendor}'s own MCP endpoint`],
    [
      'Connects with',
      s.auth === 'oauth'
        ? `Your ${vendor} account, connected once for your org`
        : s.auth === 'key'
          ? `Credentials you paste in once${s.credentials?.length ? `: ${list(s.credentials)}` : ''}`
          : 'Nothing, it needs no credentials',
    ],
  ];
  if (s.settings?.length) rows.push(['Settings', list(s.settings)]);
  if (s.egress) {
    const hosts = [...s.egress, ...(s.egressFromSetting ? ['the host you enter at install'] : [])];
    rows.push([
      'May connect to',
      hosts.length ? (
        <span className="ct-hosts">
          {hosts.map((h) => (
            <code key={h}>{h}</code>
          ))}
        </span>
      ) : (
        'No outside hosts'
      ),
    ]);
  }
  rows.push(['Category', CATEGORY_LABELS[s.category]]);
  return rows;
}

/**
 * /mcp-servers/<slug>: one catalog entry. The facts come from the catalog repo
 * through src/catalog/catalog.json; the write-up, when there is one, from
 * src/content/servers/<slug>.mdx. Pages without a guide are noindex
 * (vite-plugins/routes.ts).
 */
export default function ServerPage({ server }: { server: CatalogServer }) {
  const guide = SERVER_GUIDES[server.slug];
  const vendor = server.publisher ?? server.name;
  const related = CATALOG.filter((s) => s.category === server.category && s.slug !== server.slug).slice(0, 4);

  return (
    <PageShell>
      <PageBody>
        <Link className="s-link ct-back" to={CATALOG_PATH}>
          <ArrowLeft size={14} weight="bold" /> All MCP servers
        </Link>
        <div className="ct-detail-hero">
          <ServerIcon server={server} size={32} />
          <PageHero
            title={`${server.name} MCP server`}
            lede={server.description}
            actions={
              <>
                <a
                  className="s-btn s-btn-pri"
                  href={signupFrom(`catalog-${server.slug}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Connect in Statio
                </a>
                {server.docsUrl && (
                  <a className="s-btn s-btn-sec" href={server.docsUrl} target="_blank" rel="noopener noreferrer">
                    Vendor docs
                  </a>
                )}
              </>
            }
          />
        </div>

        <Section
          id="facts"
          title="How it connects"
          lede={
            <>
              New to MCP?{' '}
              <Link className="s-link" to="/tutorials/what-is-mcp">
                What is an MCP server?
              </Link>
            </>
          }
        >
          <dl className="ct-facts">
            {facts(server).map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </Section>

        {guide && (
          <section className="pg-sec ct-guide" aria-label={`${server.name} guide`}>
            <p className="ct-guide-meta">
              Checked {formatDate(guide.updated)} against {guide.tested}
            </p>
            <div className="bl-body">
              <guide.Content components={guideComponents} />
            </div>
          </section>
        )}

        <Section id="statio" title="What Statio adds">
          <Prose>
            <ul>
              <li>
                You choose which of its tools each agent may call. A tool you hold back is removed from that agent's
                tool list, and a call to it is refused.
              </li>
              <li>
                {server.auth === 'oauth'
                  ? `Agents get a Statio token, never the ${vendor} one.`
                  : server.auth === 'key'
                    ? 'The credentials stay encrypted in Statio, so they never sit in an agent config file.'
                    : 'Each agent still connects with its own Statio token, which you can suspend or revoke.'}
              </li>
              <li>
                Every call is recorded with the agent that made it, when, how long it took and whether it succeeded.
              </li>
              {server.egress && (
                <li>
                  The outbound list above is enforced outside the server's container, so it cannot reach any other host.
                </li>
              )}
            </ul>
          </Prose>
        </Section>

        {related.length > 0 && (
          <Section id="related" title={`More ${CATEGORY_LABELS[server.category]} servers`}>
            <ul className="ct-grid">
              {related.map((s) => (
                <ServerCard key={s.slug} server={s} />
              ))}
            </ul>
          </Section>
        )}
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
